import React, { useEffect, useState, useMemo } from "react";

import {
  FileText,
  Plus,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Check,
  X,
  MoreHorizontal,
  ExternalLink,
  Pencil,
  Trash2,
  Copy,
  Home,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Hint } from "@/components/ui/hint";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UnsavedChangesDialog } from "@/components/visual-editor/unsaved-changes-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { cn } from "@/lib/utils";
import { usePages } from "@/hooks/use-pages";
import { useToast } from "@/hooks/use-toast";
import { useVisualEditorHistoryStore } from "@/stores/visualEditorHistoryStore";
import { validateRouteName } from "@/lib/visual-editor/validate-route";

interface PagesPanelProps {
  devContainerId: string;
  currentPath?: string;
  onNavigate?: (route: string) => void;
}

export default function PagesPanel({
  devContainerId,
  currentPath,
  onNavigate,
}: PagesPanelProps): React.ReactElement {
  const {
    pages,
    isLoading,
    error,
    fetchPages,
    createPage,
    deletePage,
    renamePage,
    duplicatePage,
    setAsHomepage,
  } = usePages(devContainerId);
  const { toast } = useToast();

  const [isCreating, setIsCreating] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newRouteName, setNewRouteName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  // Rename state
  const [renamingRoute, setRenamingRoute] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete state: confirmDeleteRoute = dialog open, deleteInProgressRoute = spinner on row
  const [confirmDeleteRoute, setConfirmDeleteRoute] = useState<string | null>(null);
  const [deleteInProgressRoute, setDeleteInProgressRoute] = useState<string | null>(null);

  // Duplicate state — stores the route being duplicated (null = idle)
  const [duplicatingRoute, setDuplicatingRoute] = useState<string | null>(null);

  // Set as homepage state
  const [settingHomepageRoute, setSettingHomepageRoute] = useState<string | null>(null);

  // Unsaved changes navigation guard
  const [pendingNavigateRoute, setPendingNavigateRoute] = useState<string | null>(null);
  const hasUnsavedChanges = useVisualEditorHistoryStore((s) => s.history.length > 0);
  const clearHistory = useVisualEditorHistoryStore((s) => s.clearHistory);

  // Real-time validation as user types
  const validationError = useMemo(
    () => (newRouteName.trim() ? validateRouteName(newRouteName) : null),
    [newRouteName],
  );

  const renameValidationError = useMemo(
    () => (renameValue.trim() ? validateRouteName(renameValue) : null),
    [renameValue],
  );

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleCreatePage = async (): Promise<void> => {
    const trimmed = newRouteName.trim();
    if (!trimmed || validationError) return;

    setIsCreating(true);
    setCreateError(null);
    const result = await createPage(trimmed);
    if (result.success) {
      setNewRouteName("");
      setShowCreateInput(false);
    } else {
      setCreateError(result.error || "Failed to create page");
    }
    setIsCreating(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      handleCreatePage();
    } else if (e.key === "Escape") {
      setShowCreateInput(false);
      setNewRouteName("");
    }
  };

  const handleDeletePage = async (route: string): Promise<void> => {
    setConfirmDeleteRoute(null); // close dialog first
    setDeleteInProgressRoute(route); // show spinner on row
    const result = await deletePage(route);
    if (!result.success) {
      toast({
        title: "Failed to delete page",
        description: result.error || `Could not delete ${route}`,
        variant: "destructive",
      });
    }
    setDeleteInProgressRoute(null);
  };

  const handleStartRename = (route: string): void => {
    setRenamingRoute(route);
    setRenameValue(route.replace(/^\//, ""));
  };

  const handleRename = async (): Promise<void> => {
    if (!renamingRoute || !renameValue.trim() || renameValidationError) return;

    setIsRenaming(true);
    const result = await renamePage(renamingRoute, renameValue.trim());
    if (result.success) {
      setRenamingRoute(null);
      setRenameValue("");
    } else {
      toast({
        title: "Failed to rename page",
        description: result.error || `Could not rename ${renamingRoute}`,
        variant: "destructive",
      });
      setRenamingRoute(null);
      setRenameValue("");
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      setRenamingRoute(null);
      setRenameValue("");
    }
  };

  const handleNavigate = (route: string): void => {
    if (hasUnsavedChanges) {
      setPendingNavigateRoute(route);
      return;
    }
    onNavigate?.(route);
  };

  const handleDuplicatePage = async (route: string): Promise<void> => {
    setDuplicatingRoute(route);
    const result = await duplicatePage(route);
    if (result.success) {
      handleNavigate(result.route);
    } else {
      toast({
        title: "Failed to duplicate page",
        description: result.error || `Could not duplicate ${route}`,
        variant: "destructive",
      });
    }
    setDuplicatingRoute(null);
  };

  const handleSetAsHomepage = async (route: string): Promise<void> => {
    setSettingHomepageRoute(route);
    const result = await setAsHomepage(route);
    if (result.success) {
      toast({
        title: "Homepage updated",
        description: `${route} is now the homepage`,
      });
      handleNavigate("/");
    } else {
      toast({
        title: "Failed to set homepage",
        description: result.error || `Could not set ${route} as homepage`,
        variant: "destructive",
      });
    }
    setSettingHomepageRoute(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="group/header h-12 px-4 border-b border-border flex items-center bg-background shrink-0">
        <FileText className="w-4 h-4 text-muted-foreground mr-2 shrink-0" />
        <span className="text-sm font-medium">Pages</span>
        {pages.length > 0 && (
          <span className="ml-2 text-[10px] text-muted-foreground bg-muted/80 min-w-[18px] h-[18px] inline-flex items-center justify-center px-1.5 rounded-full tabular-nums">
            {pages.length}
          </span>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-1 opacity-0 group-hover/header:opacity-100 transition-opacity">
          <Hint text="Refresh" side="bottom">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={fetchPages}
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </Hint>
          <Hint text="New Page" side="bottom">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setShowCreateInput(true)}
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </Hint>
        </div>
      </div>

      {/* Create input */}
      {showCreateInput && (
        <div className="px-3 py-2 bg-muted/30 border-b border-border/50">
          <div className="flex items-center gap-1">
            <Input
              value={newRouteName}
              onChange={(e) => {
                setNewRouteName(e.target.value);
                setCreateError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Route name (e.g. about, blog/categories)"
              className={cn(
                "h-7 text-xs",
                (validationError || createError) &&
                  "border-destructive focus-visible:ring-destructive ring-1 ring-destructive/50",
              )}
              autoFocus
              disabled={isCreating}
            />
            <Hint text="Create" side="bottom">
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 shrink-0"
                onClick={handleCreatePage}
                disabled={
                  !newRouteName.trim() || !!validationError || isCreating
                }
              >
                {isCreating ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
              </Button>
            </Hint>
            <Hint text="Cancel" side="bottom">
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 shrink-0"
                onClick={() => {
                  setShowCreateInput(false);
                  setNewRouteName("");
                  setCreateError(null);
                }}
                disabled={isCreating}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </Hint>
          </div>
          {(validationError || createError) && (
            <p className="text-[10px] text-destructive mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              {validationError || createError}
            </p>
          )}
        </div>
      )}

      {/* Fetch error */}
      {error && (
        <div className="px-3 py-2 text-[10px] text-destructive bg-destructive/5 border-b border-destructive/10 flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {error}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">
            Loading pages...
          </span>
        </div>
      ) : pages.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 px-4 text-center">
          <div className="rounded-lg bg-muted/50 p-3 mb-3">
            <FileText className="w-5 h-5 text-muted-foreground" />
          </div>
          <p className="text-xs font-medium">No Pages</p>
          <p className="text-[10px] text-muted-foreground mt-1">
            Create your first page to get started
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 h-7 text-xs gap-1.5"
            onClick={() => setShowCreateInput(true)}
          >
            <Plus className="w-3 h-3" />
            New Page
          </Button>
        </div>
      ) : (
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {pages.map((page) => (
              <div key={page.filePath}>
                {renamingRoute === page.route ? (
                  <div className="px-2.5 py-2 rounded-md bg-accent/50 border border-border/50">
                    <div className="flex items-center gap-1">
                      <Input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={handleRenameKeyDown}
                        className={cn(
                          "h-7 text-xs",
                          renameValidationError &&
                            "border-destructive focus-visible:ring-destructive",
                        )}
                        autoFocus
                        disabled={isRenaming}
                      />
                      <Hint text="Save" side="bottom">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 shrink-0"
                          onClick={handleRename}
                          disabled={
                            !renameValue.trim() ||
                            !!renameValidationError ||
                            isRenaming
                          }
                        >
                          {isRenaming ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </Hint>
                      <Hint text="Cancel" side="bottom">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 shrink-0"
                          onClick={() => {
                            setRenamingRoute(null);
                            setRenameValue("");
                          }}
                          disabled={isRenaming}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </Hint>
                    </div>
                    {renameValidationError && (
                      <p className="text-[10px] text-destructive mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        {renameValidationError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div
                    className={cn(
                      "w-full text-left px-2.5 py-2 rounded-md hover:bg-accent/50 transition-colors",
                      "flex items-center gap-2.5 group cursor-pointer",
                      currentPath === page.route &&
                        "bg-accent border-l-2 border-primary",
                    )}
                    onClick={() => handleNavigate(page.route)}
                  >
                    <div className="w-8 h-8 rounded-md bg-muted/80 flex items-center justify-center shrink-0">
                      {duplicatingRoute === page.route ||
                      settingHomepageRoute === page.route ||
                      deleteInProgressRoute === page.route ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                      ) : (
                        <FileText
                          className={cn(
                            "w-3.5 h-3.5",
                            currentPath === page.route
                              ? "text-primary"
                              : "text-muted-foreground",
                          )}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate flex items-center gap-1.5">
                        <span className="font-mono">{page.route}</span>
                        {page.route === "/" && (
                          <Hint text="Homepage" side="right">
                            <span>
                              <Home className="w-3 h-3 text-primary shrink-0" />
                            </span>
                          </Hint>
                        )}
                        {!page.registered && (
                          <Hint
                            text="Route not registered in App.tsx"
                            side="right"
                          >
                            <span>
                              <AlertTriangle className="w-3 h-3 text-yellow-500 shrink-0" />
                            </span>
                          </Hint>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate font-mono mt-0.5">
                        {page.filePath}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate(page.route);
                          }}
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-2" />
                          Navigate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartRename(page.route);
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!!duplicatingRoute}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicatePage(page.route);
                          }}
                        >
                          {duplicatingRoute === page.route ? (
                            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 mr-2" />
                          )}
                          {duplicatingRoute === page.route ? "Duplicating..." : "Duplicate"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {page.route !== "/" && (
                          <DropdownMenuItem
                            disabled={!!settingHomepageRoute}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetAsHomepage(page.route);
                            }}
                          >
                            {settingHomepageRoute === page.route ? (
                              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                            ) : (
                              <Home className="w-3.5 h-3.5 mr-2" />
                            )}
                            <span className="whitespace-nowrap">{settingHomepageRoute === page.route ? "Setting..." : "Set as Homepage"}</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteRoute(page.route);
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!confirmDeleteRoute}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteRoute(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete page</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the page at{" "}
              <span className="font-medium text-foreground">
                {confirmDeleteRoute}
              </span>{" "}
              and remove its route registration. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDeleteRoute && handleDeletePage(confirmDeleteRoute)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unsaved visual editor changes dialog (page navigation) */}
      <UnsavedChangesDialog
        open={!!pendingNavigateRoute}
        onCancel={() => setPendingNavigateRoute(null)}
        onDiscard={() => {
          if (pendingNavigateRoute) {
            clearHistory();
            onNavigate?.(pendingNavigateRoute);
          }
          setPendingNavigateRoute(null);
        }}
        description="You have unsaved changes in the visual editor. Are you sure you want to navigate to a different page without saving?"
        actionLabel="Discard & Navigate"
      />
    </div>
  );
}
